import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { BeatLoader } from 'react-spinners';
import '../../../../../styles/count_graphs.css';
import { url } from '../../../../../api/endpointURL';
import MonthlyOccupancyChart from './MonthlyOccupancyChart';
import WeeklyOccupancyChart from './WeeklyOccupancyChart';
import DailyOccupancyChart from './DailyOccupancyChart';
import { getPercentageIncrease,
    filterMonthlyDataByCamera,
    filterWeeklyDataByCamera,
    filterDailyDataByCamera
} from '../../baseLpr/baseLprUtils';
import { 
    checkForEmptyData,
    getTotalForPeriod,
    getYearlyTotal
} from '../baseSuiteUtils';

const PeopleOccupancyAnalytics = ({
    selectedSite,
    selectedCameraId, 
    selectedYear,
    handleOccupancyDataState
}) => {

    const [monthlyOccupancyData, setMonthlyOccupancyData] = useState([]);
    const [monthlyLoading, setMonthlyLoading] = useState(false);

    const [weeklyOccupancyData, setWeeklyOccupancyData] = useState([]);
    const [weeklyLoading, setWeeklyLoading] = useState(false);
    
    const [dailyOccupancyData, setDailyOccupancyData] = useState([]);
    const [dailyLoading, setDailyLoading] = useState(false);

    const [selectedMonth, setSelectedMonth] = useState();
    const [selectedWeek, setSelectedWeek] = useState();
    const [selectedWeekStartDate, setSelectedWeekStarDate] = useState();
    const [selectedRelativeWeek, setSelectedRelativeWeek] = useState();
    const [selectedDate, setSelectedDate] = useState();
    const [selectedDayOfWeek, setSelectedDayOfWeek] = useState();

    const [totalAlerts, setTotalAlerts] = useState();

    const monthlyAbortControllerRef = useRef(null);
    const weeklyAbortControllerRef = useRef(null);
    const dailyAbortControllerRef = useRef(null);

    const [initialFetchCompleted, setInitialFetchCompleted] = useState(false);
    const [showOccupancy, setShowOccupancy] = useState(true);

    const prevCameraRef = useRef(selectedCameraId);

    const analyticType = 'peopleOccupancy';

    // Filter by camera
    const aggregatedMonthlyData = useMemo(() => 
        filterMonthlyDataByCamera(monthlyOccupancyData, selectedCameraId, 'total_count'),
        [monthlyOccupancyData, selectedCameraId]
    );

    const aggregatedWeeklyData = useMemo(() => 
        filterWeeklyDataByCamera(weeklyOccupancyData, selectedCameraId, 'total_count'),
        [weeklyOccupancyData, selectedCameraId]
    );

    const aggregatedDailyData = useMemo(() => 
        filterDailyDataByCamera(dailyOccupancyData, selectedCameraId, 'total_count'),
        [dailyOccupancyData, selectedCameraId]
    );

    //Hiding when empty
    
    useEffect(() => {
        const cameraChanged = prevCameraRef.current !== selectedCameraId;
        prevCameraRef.current = selectedCameraId;

        if (cameraChanged) {
            setInitialFetchCompleted(false);
        }
    }, [selectedCameraId]);

    useEffect(() => {
        if (aggregatedMonthlyData.length) {
            setInitialFetchCompleted(true);
        }
    }, [aggregatedMonthlyData])

    const hasActualData = useCallback(() => {
        const hasData = checkForEmptyData(aggregatedMonthlyData);
        if (!hasData) {
            return false;
        } else {
            return true;
        }


    }, [aggregatedMonthlyData]);

    //Fetching data

    const fetchMonthlyOccupancyData = useCallback(async () => {
        if (monthlyAbortControllerRef.current) {
            monthlyAbortControllerRef.current.abort();
        }

        monthlyAbortControllerRef.current = new AbortController();
        const { signal } = monthlyAbortControllerRef.current;
        try {
            setMonthlyLoading(true);

            const response = await fetch(`${url}/occupancy/monthlyOccupancy?year=${selectedYear}&hwid=${selectedSite?.hwid}&analyticType=${analyticType}`, { signal });
            if (!response.ok) {
                throw new Error(`Server responded with status: ${response.status}`);
            }
            const responseData = await response.json();

            setMonthlyLoading(false);

            setMonthlyOccupancyData(responseData);

            setInitialFetchCompleted(true);

            if (responseData.length) {
                const latestMonthWithData = [...responseData]
                    .reverse()
                    .find(month => month.total_count > 0);

                if (latestMonthWithData) {
                    setSelectedMonth(latestMonthWithData.month);
                }
            }



        } catch (e) {
            if (e.name === 'AbortError') {
                // console.log("Fetch monthly people occupancy data aborted");
            }
            // console.log(e)
        } finally {
            setMonthlyLoading(false);
        }
    }, [selectedYear, selectedSite.hwid]);

    const fetchWeeklyOccupancyData = useCallback(async () => {
        if (weeklyAbortControllerRef.current) {
            weeklyAbortControllerRef.current.abort();
        }

        weeklyAbortControllerRef.current = new AbortController();
        const { signal } = weeklyAbortControllerRef.current;
        try {

            setWeeklyLoading(true);

            const response = await fetch(`${url}/occupancy/weeklyOccupancy?year=${selectedYear}&month=${selectedMonth}&hwid=${selectedSite?.hwid}&analyticType=${analyticType}`, { signal });
            if (!response.ok) {
                throw new Error(`Server responded with status: ${response.status}`);
            }
            const responseData = await response.json();

            setWeeklyLoading(false);

            setWeeklyOccupancyData(responseData);

            if (responseData.length) {
                const latestWeekWithData = [...responseData]
                    .reverse()
                    .find(week => week.total_count > 0);

                if (latestWeekWithData) {
                    setSelectedWeek(latestWeekWithData.week);
                    setSelectedWeekStarDate(latestWeekWithData.week_start_date);
                    setSelectedRelativeWeek(latestWeekWithData.relative_week_number);
                }

            }

        } catch (e) {
            if (e.name === 'AbortError') {
                // console.log("fetching weekly people occupancy data aborted");
            }
            // console.log(e)
        } finally {
            setWeeklyLoading(false);
        }
    }, [selectedYear, selectedMonth, selectedSite.hwid]);

    const fetchDailyOccupancyData = useCallback(async () => {
        if (dailyAbortControllerRef.current) {
            dailyAbortControllerRef.current.abort();
        }

        dailyAbortControllerRef.current = new AbortController();
        const { signal } = dailyAbortControllerRef.current;
        try {
            setDailyLoading(true);

            const response = await fetch(`${url}/occupancy/dailyOccupancy?year=${selectedYear}&month=${selectedMonth}&week=${selectedWeek}&hwid=${selectedSite?.hwid}&analyticType=${analyticType}`, {signal});
            if (!response.ok) {
                throw new Error(`Server responded with status: ${response.status}`);
            }
            const responseData = await response.json();


            setDailyLoading(false);

            setDailyOccupancyData(responseData);

            if (responseData.length) {
                const latestDayWithData = [...responseData]
                    .reverse()
                    .find(day => day.total_count > 0);

                if (latestDayWithData) {
                    setSelectedDate(latestDayWithData.day_number);
                    setSelectedDayOfWeek(latestDayWithData.day);
                }
            }

        } catch (e) {
            if (e.name === "AbortError") {
                // console.log("daily people occupancy data fetch aborted.");
            } 
            // console.log(e)
        } finally {
            setDailyLoading(false);
        }
    }, [selectedYear, selectedMonth, selectedWeek, selectedSite?.hwid]);
    
    useEffect(() => {
        return () => {
            if (monthlyAbortControllerRef.current) monthlyAbortControllerRef.current.abort();
            if (weeklyAbortControllerRef.current) weeklyAbortControllerRef.current.abort();
            if (dailyAbortControllerRef.current) dailyAbortControllerRef.current.abort();
        };
    }, []);

    useEffect(() => {
        if (selectedYear && selectedSite?.hwid) {
            fetchMonthlyOccupancyData();
        }
    }, [selectedYear, selectedSite?.hwid, fetchMonthlyOccupancyData]);

    useEffect(() => {
        if (initialFetchCompleted) {
            const hasData = hasActualData();
            setShowOccupancy(hasData);

            if (handleOccupancyDataState) {
                handleOccupancyDataState(hasData);
            }
        }
    }, [initialFetchCompleted, hasActualData, handleOccupancyDataState]);

    useEffect(() => {
        if (monthlyOccupancyData.length > 0) {
            let total = getYearlyTotal(monthlyOccupancyData, 'total_count');
            setTotalAlerts(total);
        }
    }, [monthlyOccupancyData])

    useEffect(() => {
        if (selectedMonth > 0) {
            fetchWeeklyOccupancyData();
        }

    }, [selectedYear, selectedMonth, selectedSite?.hwid, monthlyOccupancyData, fetchWeeklyOccupancyData]);

    useEffect(() => {
        if (selectedWeek) {
            fetchDailyOccupancyData();
        }
    }, [selectedYear, selectedMonth, selectedWeek, selectedSite?.hwid, weeklyOccupancyData, fetchDailyOccupancyData]);


    return (
        <>
            {
                showOccupancy ?
                    <>

                        <div className='graphs-container'>
                            <div className='monthly-bar-chart-container'>
                                <MonthlyOccupancyChart
                                    selectedYear={selectedYear}
                                    monthlyOccupancyData={aggregatedMonthlyData}
                                    monthlyDataLoading={monthlyLoading}
                                    selectedMonth={selectedMonth}
                                    handleMonthlyBarClick={(month) => {
                                        setSelectedMonth(month);
                                        fetchWeeklyOccupancyData();
                                    }}
                                    monthlyPercentageIncrease={getPercentageIncrease(aggregatedMonthlyData, selectedMonth, 'month', 'total_count')}
                                    monthlyAlerts={getTotalForPeriod(aggregatedMonthlyData, selectedMonth, 'month', 'total_count')}
                                    occupancyType={'People'}
                                />
                            </div>
                            <div className='weekly-bar-chart-container'>
                                <WeeklyOccupancyChart
                                    selectedYear={selectedYear}
                                    weeklyOccupancyData={aggregatedWeeklyData}
                                    weeklyDataLoading={weeklyLoading}
                                    selectedMonth={selectedMonth}
                                    selectedWeek={selectedWeek}
                                    relativeWeekNumber={selectedRelativeWeek}
                                    handleWeeklyBarClick={(week) => {
                                        setSelectedWeek(week);
                                        fetchDailyOccupancyData();
                                    }}
                                    weeklyPercentageIncrease={getPercentageIncrease(aggregatedWeeklyData, selectedWeek, 'week', 'total_count')}
                                    weeklyAlerts={getTotalForPeriod(aggregatedWeeklyData, selectedWeek, 'week', 'total_count')}
                                    occupancyType={'People'}
                                />
                            </div>
                            <div className='daily-bar-chart-container'>
                                <DailyOccupancyChart
                                    selectedYear={selectedYear}
                                    dailyOccupancyData={aggregatedDailyData}
                                    dailyDataLoading={dailyLoading}
                                    selectedMonth={selectedMonth}
                                    selectedWeek={selectedWeek}
                                    selectedWeekStartDate={selectedWeekStartDate}
                                    relativeWeekNumber={selectedRelativeWeek}
                                    selectedDate={selectedDate}
                                    selectedDayOfWeek={selectedDayOfWeek}
                                    dailyPercentageIncrease={getPercentageIncrease(aggregatedDailyData, selectedDate, 'day_number', 'total_count')}
                                    dailyAlerts={getTotalForPeriod(aggregatedDailyData, selectedDate, 'day_number', 'total_count')}
                                    occupancyType={'People'}
                                />
                            </div>
                        </div>
                    </>
                    :
                    <div style={{ display: 'none'}}>

                    </div>
            }
        </>
    );
}

export default PeopleOccupancyAnalytics;